import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AttributePill } from "../scout-request/ScoutPills";
import { formatPositionAge, formatScoutHorizon } from "../../utils/scoutFormat";
import { feeFramePillLabel } from "../../utils/scoutFeeFrame";

// Request-Share chat card (design 5d, AGENT shares a scout request). Rendered from
// the `scout_request_share` attachment (payload contract M4-FE-CONTRACT §1). The
// club is the deliberately open surface (Invariant 1). CTA "Open request" is a
// NAVIGATION target (cta_path → the recommend flow), never an API call.
const ClubCrest = ({ club }) => {
  const [broken, setBroken] = React.useState(false);
  const initials = (club?.name || "")
    .replace(/[^A-Za-z ]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "FC";
  if (club?.logo && !broken) {
    return <img src={club.logo} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" onError={() => setBroken(true)} />;
  }
  return (
    <span className="w-9 h-9 rounded-lg bg-[#1E3A5F] text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0">
      {initials}
    </span>
  );
};

const ScoutRequestShareCard = (props) => {
  const navigate = useNavigate();
  const meta = props.attachments?.[0]?.metadata || {};
  const club = meta.club || {};
  const feeLabel = feeFramePillLabel(meta.fee_frame);

  return (
    <div className="max-w-[340px] bg-white border border-[#E5E7EB] rounded-xl p-4 shadow-[0_1px_2px_rgba(17,24,39,0.05)]">
      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9CA3AF] mb-2">
        {meta.label || "Scout request"}
      </p>

      <div className="flex items-center gap-2.5">
        <ClubCrest club={club} />
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-[#111827] truncate">{club.name}</p>
          <p className="text-[11.5px] text-[#6B7280] flex items-center gap-1">
            {club.flag_url && <img src={club.flag_url} alt="" className="w-4 h-[11px] rounded-[2px] object-cover" />}
            {club.country}
          </p>
        </div>
      </div>

      <p className="text-[13.5px] font-semibold text-[#111827] mt-3">{formatPositionAge(meta)}</p>
      {meta.horizon && (
        <p className="text-[12px] text-[#6B7280] mt-0.5">
          Horizon: {formatScoutHorizon(meta.horizon)}
          {meta.level_expectation ? ` · ${meta.level_expectation}` : ""}
        </p>
      )}

      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {meta.preferred_foot && <AttributePill>{`${meta.preferred_foot[0].toUpperCase()}${meta.preferred_foot.slice(1)} foot`}</AttributePill>}
        {meta.min_height_cm && <AttributePill>{`≥ ${meta.min_height_cm} cm`}</AttributePill>}
        {feeLabel && <AttributePill>{feeLabel}</AttributePill>}
        {meta.loan_possible && <AttributePill>Loan possible</AttributePill>}
      </div>

      {/* CTA only when a deep-link target is present (symmetry with the forward
          card — never render a dead button). */}
      {meta.cta_path && (
        <button
          type="button"
          onClick={() => navigate(meta.cta_path)}
          className="mt-3.5 w-full flex items-center justify-center gap-1.5 rounded-lg bg-[#2283FF] text-white text-[12.5px] font-semibold py-2 hover:bg-[#1B6FE0]"
        >
          Open request
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default ScoutRequestShareCard;
